import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { brazilRiskWarning, legalResources } from '../features/legal/legal-resources';
import { Badge } from '../shared/ui/badge';
import { Wordmark } from '../shared/ui/wordmark';
import { LocaleSwitcher } from './locale-switcher';

const columnClass = 'flex flex-col items-start gap-3';
const headingClass = 'text-sm font-semibold text-content';
const linkClass =
  'text-sm text-muted hover:text-accent-ink hover:underline hover:underline-offset-4';

export const SiteFooter = () => {
  const { t } = useTranslation();
  const { locale } = useParams();

  return (
    <footer className="border-t border-line bg-raised">
      <div className="mx-auto w-full max-w-6xl px-4 pt-14 pb-6 sm:px-6">
        <div className="grid gap-10 pb-12 sm:grid-cols-2 lg:grid-cols-[1.4fr_0.8fr_1fr_1.2fr]">
          <div className="sm:col-span-2 lg:col-span-1">
            <Wordmark to={`/${locale}/`} size="lg" />
            <p className="mt-5 max-w-[25ch] text-sm text-muted">{t('footer.tagline')}</p>
            <div className="mt-5">
              <Badge>18+</Badge>
            </div>
          </div>
          <nav className={columnClass} aria-label={t('footer.product')}>
            <h2 className={headingClass}>{t('footer.product')}</h2>
            <Link className={linkClass} to={`/${locale}/sign-up`}>
              {t('nav.signUp')}
            </Link>
            <a className={linkClass} href={`/${locale}/#how-it-works`}>
              {t('landing.hero.secondaryAction')}
            </a>
            <a className={linkClass} href={`/${locale}/#pricing`}>
              {t('nav.pricing')}
            </a>
          </nav>
          <nav className={columnClass} aria-label={t('footer.transparency')}>
            <h2 className={headingClass}>{t('footer.transparency')}</h2>
            <Link className={linkClass} to={`/${locale}/terms/`}>
              {t('legal.terms.title')}
            </Link>
            <Link className={linkClass} to={`/${locale}/privacy/`}>
              {t('legal.privacy.title')}
            </Link>
            <Link className={linkClass} to={`/${locale}/responsible-gambling/`}>
              {t('legal.responsible.title')}
            </Link>
          </nav>
          <nav className={columnClass} aria-label={t('footer.resources')}>
            <h2 className={headingClass}>{t('footer.support')}</h2>
            {legalResources.slice(0, 3).map(({ label, href }) => (
              <a key={label} className={linkClass} href={href}>
                {t(`footer.${label}`)}
              </a>
            ))}
          </nav>
        </div>
        <div className="border-y border-line py-7">
          <p className="text-base font-semibold text-content" lang="pt-BR">
            {brazilRiskWarning}
          </p>
          <div className="mt-6 grid gap-6 text-sm leading-relaxed text-muted sm:grid-cols-2 sm:gap-12">
            <div className="space-y-3">
              <p>{t('footer.message')}</p>
              <p>{t('footer.responsibility')}</p>
              <p>{t('footer.noIncome')}</p>
            </div>
            <div className="space-y-3">
              <p>{t('footer.independent')}</p>
              <p>{t('footer.help')}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap justify-between gap-4 pt-6 text-sm text-muted">
          <p>
            © {new Date().getUTCFullYear()} {t('app.name')}. {t('footer.rights')}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <LocaleSwitcher placement="above" showName />
            <a className={linkClass} href={legalResources[3].href}>
              {t('footer.regulation')}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
